import {
  Badge,
  Box,
  Button,
  Collapse,
  type CollapseProps,
  Flex,
  HStack,
  Icon,
  IconButton,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { FC, PropsWithChildren } from 'react'
import { EditIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import { useEffect, useState } from 'react'
import { FaBuilding } from 'react-icons/fa'
import { FiUsers } from 'react-icons/fi'
import type { Location, PrescriberProfile } from '../../../types/location'
import { formatLocationAddress } from '../../../types/location'
import { DeactivateLocationPopover } from './DeactivateLocationPopover'
import { LocationDetailPanel } from './LocationDetailPanel'
import { ManagePrescribersPanel } from './ManagePrescribersPanel'
import { PrescriberAvatarGroup } from './PrescriberAvatarGroup'

const TypedCollapse = Collapse as FC<PropsWithChildren<CollapseProps>>

interface LocationCardProps {
  location: Location
  prescribers?: PrescriberProfile[]
  allPrescribersAutoAssigned?: boolean
  onEdit?: (location: Location) => void
  onToggleActive?: (location: Location) => Promise<boolean>
  onAssignPrescribers?: (location: Location) => void
}

export const LocationCard = ({
  location,
  prescribers = [],
  allPrescribersAutoAssigned = false,
  onEdit,
  onToggleActive,
  onAssignPrescribers,
}: LocationCardProps) => {
  const address = formatLocationAddress(location)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isManageOpen, setIsManageOpen] = useState(false)
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false)
  const [optimisticStatus, setOptimisticStatus] = useState<boolean | null>(null)

  const displayStatus = optimisticStatus ?? location.status

  useEffect(() => {
    if (optimisticStatus !== null && location.status === optimisticStatus) {
      setOptimisticStatus(null)
    }
  }, [location.status, optimisticStatus])

  const handleToggleDetail = () => {
    setIsDetailOpen((previous) => !previous)
    if (!isDetailOpen) setIsManageOpen(false)
  }

  const performToggle = async () => {
    const newStatus = !location.status
    setOptimisticStatus(newStatus)

    const success = await onToggleActive?.(location)
    if (!success) {
      setOptimisticStatus(null)
    }
  }

  const handleToggleActive = () => {
    if (location.status) {
      setIsDeactivateOpen(true)
    } else {
      performToggle()
    }
  }

  const handleConfirmDeactivate = () => {
    setIsDeactivateOpen(false)
    performToggle()
  }

  const handleEdit = () => {
    onEdit?.(location)
  }

  const handleManage = () => {
    setIsManageOpen((previous) => !previous)
    if (!isManageOpen) setIsDetailOpen(false)
  }

  const handleAssign = () => {
    onAssignPrescribers?.(location)
  }

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px"
      borderColor="gray.200"
      p={6}
      transition="box-shadow 0.2s"
      _hover={{ boxShadow: 'sm' }}
    >
      <Flex justify="space-between" align="flex-start">
        <HStack spacing={4} align="flex-start">
          <Flex bg="blue.50" borderRadius="lg" p={3} align="center" justify="center" flexShrink={0}>
            <Icon as={FaBuilding} color="blue.500" boxSize={5} />
          </Flex>
          <VStack align="flex-start" spacing={1}>
            <HStack spacing={2}>
              <Text fontWeight="bold" fontSize="md" color="gray.900">
                {location.name}
              </Text>
              {location.isDefault && (
                <Badge
                  colorScheme="green"
                  fontSize="xs"
                  fontWeight="semibold"
                  borderRadius="md"
                  px={2}
                  py={0.5}
                >
                  Default
                </Badge>
              )}
            </HStack>
            {address && (
              <Text fontSize="sm" color="gray.500">
                {address}
              </Text>
            )}
          </VStack>
        </HStack>

        <HStack spacing={3}>
          {!location.isDefault && (
            <DeactivateLocationPopover
              locationName={location.name}
              isOpen={isDeactivateOpen}
              onOpen={() => setIsDeactivateOpen(true)}
              onClose={() => setIsDeactivateOpen(false)}
              onConfirm={handleConfirmDeactivate}
            >
              <HStack spacing={2}>
                <Text fontSize="sm" color="gray.600">
                  Active
                </Text>
                <Switch
                  colorScheme="blue"
                  isChecked={displayStatus}
                  isDisabled={optimisticStatus !== null}
                  onChange={handleToggleActive}
                  aria-label={`Toggle ${location.name} active status`}
                />
              </HStack>
            </DeactivateLocationPopover>
          )}
          <IconButton
            aria-label={`View ${location.name} details`}
            icon={isDetailOpen ? <ViewOffIcon /> : <ViewIcon />}
            variant="ghost"
            size="sm"
            color={isDetailOpen ? 'blue.500' : 'gray.400'}
            border="1px"
            borderColor={isDetailOpen ? 'blue.200' : 'gray.200'}
            borderRadius="md"
            _hover={{ color: 'blue.500', borderColor: 'blue.200' }}
            onClick={handleToggleDetail}
          />
          <IconButton
            aria-label={`Edit ${location.name}`}
            icon={<EditIcon />}
            variant="ghost"
            size="sm"
            color="gray.400"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            _hover={{ color: 'gray.600', borderColor: 'gray.300' }}
            onClick={handleEdit}
          />
        </HStack>
      </Flex>

      <Box mt={4} pt={4} borderTop="1px" borderColor="gray.100">
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            <Text
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing="wider"
              color="gray.500"
            >
              Prescribers
            </Text>
            {prescribers.length > 0 && <PrescriberAvatarGroup prescribers={prescribers} />}
          </HStack>

          {allPrescribersAutoAssigned ? (
            <HStack spacing={1} color="gray.400">
              <Icon as={FiUsers} boxSize={4} />
              <Text fontSize="sm">All prescribers auto-assigned</Text>
            </HStack>
          ) : (
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                borderColor="gray.200"
                color="gray.600"
                bg="white"
                fontWeight="medium"
                onClick={handleManage}
                aria-label={`${isManageOpen ? 'Hide' : 'Manage'} prescribers for ${location.name}`}
              >
                {isManageOpen ? 'Hide' : 'Manage'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Icon as={FiUsers} boxSize={4} />}
                onClick={handleAssign}
                aria-label={`Assign prescribers to ${location.name}`}
              >
                Assign
              </Button>
            </HStack>
          )}
        </Flex>
      </Box>

      <TypedCollapse in={isDetailOpen} animateOpacity>
        <Box mt={4} pt={4} borderTop="1px" borderColor="gray.100">
          <LocationDetailPanel location={location} prescribers={prescribers} />
        </Box>
      </TypedCollapse>

      <TypedCollapse in={isManageOpen} animateOpacity>
        <Box mt={4} pt={4} borderTop="1px" borderColor="gray.100">
          <ManagePrescribersPanel location={location} prescribers={prescribers} />
        </Box>
      </TypedCollapse>
    </Box>
  )
}
